// Per-line "Who has this on PartsBase" panel, and the one place an operator can
// send an RFQ from this application.
//
// IT WAS READ-ONLY UNTIL PHASE B STEP 8. The old header said there was
// deliberately no RFQ button, because every send is an irreversible message to
// a real company and that belonged behind its own explicit review. That review
// now exists and lives here, in the only screen that already shows exactly who
// would be contacted:
//
//   * the checkboxes are not rendered at all unless the SERVER says this user
//     can send. `canSend` comes back from the listings route and reflects the
//     viewer's role plus every send-gate lock. The client never infers it, so
//     no button appears whose only possible answer is a refusal.
//   * sending is TWO steps. The first press arms a confirmation naming the
//     exact number of companies; nothing leaves until the second.
//   * one idempotency key is minted when the confirmation is armed and reused
//     for every retry of that same send, so a double-click, a flaky connection
//     or an impatient second press cannot message anyone twice.
//   * `delivery_unknown` is rendered as a warning that says DO NOT RESEND
//     rather than as a failure with a retry button. PartsBase may well have
//     delivered it, and a broker who resends has messaged everyone twice.
//
// Every open costs a metered PartsBase call, which drives four choices that
// differ from the otherwise-identical alt-parts panel:
//   * the query key is the PART NUMBER, not the line item, so the same part
//     opened on two lines (or after a P/N correction) is not bought twice
//   * focus/reconnect refetching is OFF and retry is OFF — a paid lookup must
//     never re-fire because someone alt-tabbed back from Gmail
//   * the server caches results, so a repeat open inside the TTL costs nothing
//     at all; the footer says which of the two this answer was
//   * the panel is not mounted at all unless the feature is switched on

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Store, ChevronRight, RefreshCw, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  api,
  type PartsBaseListingsResponse,
  type PartsBaseSendResponse,
  type LineStage,
} from '@/lib/api';
import { usePartsBaseSearchEnabled } from '@/lib/use-partsbase-search-enabled';
import { CopyButton } from './copy-button';

interface Props {
  rfqPublicId: string;
  lineItemPublicId: string;
  partNumber: string;
  stage: LineStage;
}

function fmtQty(q: number | null): string {
  return q === null ? '—' : q.toLocaleString('en-US');
}

/**
 * PartsBase reports 0 for "call for price", which the server maps to null, and
 * its `currency` field is free text — the word "dollars" is sent by CAD, AUD and
 * SGD sellers as readily as USD ones. So: never a bare `$`. A real ISO code gets
 * rendered as a code; anything else shows the amount with their raw label, and
 * the broker knows to ask.
 */
function fmtPrice(cents: number | null, currency: string | null, raw: string | null): string {
  if (cents === null) return '—';
  const amount = (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (currency) return `${amount} ${currency}`;
  return raw ? `${amount} ${raw}` : amount;
}

function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function PartsBaseListings({ rfqPublicId, lineItemPublicId, partNumber, stage }: Props) {
  const { getToken } = useAuth();
  const featureEnabled = usePartsBaseSearchEnabled();
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    // Keyed on the PART, not the line: the server uses nothing else, so this
    // collapses duplicate lines and survives a P/N correction (which would
    // otherwise serve the previous part's sellers under the new heading).
    queryKey: ['partsbase-listings', partNumber.trim().toUpperCase()],
    enabled: open && featureEnabled,
    queryFn: async () => {
      const r = await api<PartsBaseListingsResponse>(
        `/api/rfqs/${rfqPublicId}/line-items/${lineItemPublicId}/partsbase-listings`,
        { tokenGetter: () => getToken() },
      );
      // The broker just searched: refresh the Part Intelligence evidence so it
      // shows this search. The server records the search event without waiting,
      // hence the short delay. The refresh reads stored evidence only.
      setTimeout(() => void qc.invalidateQueries({ queryKey: ['part-intelligence', rfqPublicId] }), 750);
      return r;
    },
    staleTime: 5 * 60 * 1000,
    // Each of these would spend real money without the broker asking.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Non-null while a send is armed. Holds the idempotency key minted at arming
  // time so every retry of THIS send reuses it; a key minted per request would
  // be a new key each time and would protect nobody.
  const [armed, setArmed] = useState<{ key: string } | null>(null);
  const [sendResult, setSendResult] = useState<PartsBaseSendResponse | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: (key: string) =>
      api<PartsBaseSendResponse>(
        `/api/rfqs/${rfqPublicId}/line-items/${lineItemPublicId}/partsbase-send`,
        {
          method: 'POST',
          tokenGetter: () => getToken(),
          idempotencyKey: key,
          body: JSON.stringify({ inventoryIds: [...selected] }),
        },
      ),
    onSuccess: (r) => {
      setSendResult(r);
      setSendError(null);
      setArmed(null);
      setSelected(new Set());
    },
    onError: (e: unknown) => {
      setSendError((e as { message?: string })?.message ?? 'The send could not be completed.');
      setArmed(null);
    },
  });

  // Hooks all run above this line so hook order stays stable if the stage or the
  // flag changes while mounted.
  if (!featureEnabled) return null;
  if (stage !== 'received' && stage !== 'sourcing') return null;

  const listings = data?.status === 'ok' ? data.listings : [];
  const emails = [...new Set(listings.map((l) => l.seller.email).filter((e): e is string => !!e))];

  const canSend = data?.status === 'ok' && data.canSend === true;

  // Who has already been asked about this part. Keyed by inventory id, which is
  // what a send addresses — a seller with two listings may have been asked
  // about one and not the other, and conflating them would claim something
  // untrue.
  const sentByInventory = new Map(
    (data?.status === 'ok' ? data.alreadySent ?? [] : []).map((s) => [s.inventoryId, s]),
  );
  const maxRecipients = (data?.status === 'ok' ? data.maxRecipients : undefined) ?? 5;
  const overCap = selected.size > maxRecipients;

  // Selecting every listing is not the same as selecting every SELLER: one
  // seller can list the same part more than once, and each listing is a
  // separate inventoryId. The cap counts what the server will send.
  //
  // Deliberately NOT a useMemo. This sits below the two early returns above,
  // and a hook here would change hook order the moment the panel is hidden by
  // a stage change — the exact failure the comment above those returns warns
  // about. It is an `every` over at most `search_result_limit` items.
  const allSelected =
    listings.length > 0 && listings.every((l) => selected.has(l.inventoryId));

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(listings.map((l) => l.inventoryId)));

  return (
    <div className="mt-3 border border-border rounded-md bg-surface-muted/40 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium hover:bg-surface-muted/70 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Store className="w-3.5 h-3.5 text-primary-strong" />
          <span className="text-primary-strong">Who has this on PartsBase</span>
          {open && data?.status === 'ok' && (
            <span className="pill pill-info">
              {data.sellerCount} seller{data.sellerCount === 1 ? '' : 's'}
            </span>
          )}
        </span>
        <ChevronRight
          className={`w-3.5 h-3.5 text-foreground-muted transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 text-xs">
          {isLoading && <div className="text-foreground-muted py-2">Searching PartsBase…</div>}

          {isError && (
            <div className="text-destructive py-2">
              {(error as { message?: string })?.message ?? 'Lookup unavailable — try again later.'}
            </div>
          )}

          {data && data.status !== 'ok' && (
            <div className="py-2 flex items-center justify-between gap-3">
              <span className="text-foreground-muted">{data.message}</span>
              {/* The route always answers 200, so react-query treats a transient
                  failure as cached success. Without this the "try again" advice
                  names an action the panel cannot perform for five minutes. */}
              {(data.status === 'unavailable' || data.status === 'misconfigured') && (
                <button
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="flex items-center gap-1 text-primary hover:underline disabled:opacity-50"
                >
                  <RefreshCw className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`} />
                  Retry
                </button>
              )}
            </div>
          )}

          {data?.status === 'ok' && listings.length === 0 && (
            <div className="text-foreground-muted py-2">
              No seller currently lists <span className="font-mono">{partNumber}</span> on PartsBase.
              {/* Never claim a definitive negative off a capped page. */}
              {data.truncated && (
                <span className="block mt-1">
                  Note: PartsBase returned a full page of near matches for this number, so exact
                  matches may exist beyond it. Try a more specific part number.
                </span>
              )}
            </div>
          )}

          {data?.status === 'ok' && listings.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-foreground-muted">
                  {listings.length} listing{listings.length === 1 ? '' : 's'} from {data.sellerCount}{' '}
                  seller{data.sellerCount === 1 ? '' : 's'}
                  {data.truncated ? ' (first page — more may exist)' : ''}
                </span>
                {emails.length > 0 && (
                  <CopyButton value={emails.join(', ')} label="Copy seller emails" size="sm" />
                )}
              </div>

              <div className="overflow-x-auto border border-border rounded">
                <table className="w-full min-w-[860px] text-left">
                  <thead className="bg-surface-muted/60 text-foreground-muted">
                    <tr>
                      {canSend && (
                        <th className="px-2 py-1 font-medium w-8">
                          <input
                            type="checkbox"
                            aria-label="Select all listings"
                            checked={allSelected}
                            onChange={toggleAll}
                            disabled={send.isPending}
                            className="align-middle cursor-pointer"
                          />
                        </th>
                      )}
                      <th className="px-2 py-1 font-medium">Seller</th>
                      <th className="px-2 py-1 font-medium">Country</th>
                      <th className="px-2 py-1 font-medium">Cond</th>
                      <th className="px-2 py-1 font-medium text-right">Qty</th>
                      <th className="px-2 py-1 font-medium text-right">Unit price</th>
                      <th className="px-2 py-1 font-medium">Certificates</th>
                      <th className="px-2 py-1 font-medium">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map((l) => (
                      <tr
                        key={l.inventoryId}
                        className={`border-t border-border align-top ${
                          selected.has(l.inventoryId)
                            ? 'bg-primary-muted/40'
                            : sentByInventory.has(l.inventoryId)
                              ? 'bg-success-muted/40'
                              : ''
                        }`}
                      >
                        {canSend && (
                          <td className="px-2 py-1">
                            <input
                              type="checkbox"
                              aria-label={`Select ${l.seller.name ?? 'seller'}`}
                              checked={selected.has(l.inventoryId)}
                              onChange={() => toggle(l.inventoryId)}
                              disabled={send.isPending}
                              className="align-middle cursor-pointer"
                            />
                          </td>
                        )}
                        <td className="px-2 py-1">
                          <div className="text-foreground flex items-center gap-1.5">
                            {l.seller.name ?? '—'}
                            {sentByInventory.has(l.inventoryId) && (
                              <span
                                className="pill pill-success text-[9px]"
                                title={`RFQ sent ${
                                  sentByInventory.get(l.inventoryId)!.sentAt
                                    ? new Date(sentByInventory.get(l.inventoryId)!.sentAt)
                                        .toLocaleDateString()
                                    : ''
                                } on ${sentByInventory.get(l.inventoryId)!.rfqRef}`}
                              >
                                RFQ sent
                              </span>
                            )}
                          </div>
                          {l.seller.email && (
                            <div className="text-foreground-muted font-mono text-[10px]">
                              {l.seller.email}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1 text-foreground-muted">{l.seller.country ?? '—'}</td>
                        <td className="px-2 py-1 font-mono">
                          {l.conditionCode ?? l.conditionRaw ?? '—'}
                        </td>
                        <td className="px-2 py-1 text-right font-mono">{fmtQty(l.quantity)}</td>
                        <td className="px-2 py-1 text-right font-mono">
                          {fmtPrice(l.unitPriceCents, l.currency, l.currencyRaw)}
                        </td>
                        <td className="px-2 py-1 text-foreground-muted">
                          {l.seller.certificates.length ? l.seller.certificates.join(', ') : '—'}
                        </td>
                        <td className="px-2 py-1 text-foreground-muted">{fmtWhen(l.lastUpdated)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ---- send controls ------------------------------------ */}
              {canSend && (
                <div className="border-t border-border pt-2 space-y-2">
                  {sendResult ? (
                    sendResult.status === 'sent' ? (
                      <div className="flex items-start gap-2 rounded bg-success-muted p-2">
                        <CheckCircle2 className="w-4 h-4 text-success shrink-0 mt-0.5" />
                        <div>
                          <div className="text-success font-medium">{sendResult.message}</div>
                          <div className="text-foreground-muted mt-0.5">
                            PartsBase RFQ{' '}
                            <span className="font-mono">#{sendResult.partsbaseRfqId}</span> · our ref{' '}
                            <span className="font-mono">{sendResult.trackingRef}</span>
                            {sendResult.notQualifiedCount > 0 && (
                              <> · {sendResult.notQualifiedCount} listing(s) PartsBase declined</>
                            )}
                          </div>
                          <div className="text-foreground-muted mt-1">
                            Replies quoting this RFQ number will attach themselves to this line
                            automatically.
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Ambiguous, not failed. Offering a retry here is how a
                         broker messages twenty-five companies twice. */
                      <div className="flex items-start gap-2 rounded bg-warning-muted p-2">
                        <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
                        <div>
                          <div className="text-warning font-medium">Outcome unconfirmed</div>
                          <div className="text-foreground-muted mt-0.5">{sendResult.message}</div>
                        </div>
                      </div>
                    )
                  ) : armed ? (
                    <div className="rounded bg-warning-muted p-2 space-y-2">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
                        <div className="text-foreground">
                          This sends a real RFQ for{' '}
                          <span className="font-medium">
                            {(data.sendQuantity ?? 1).toLocaleString('en-US')}
                            {' × '}
                            <span className="font-mono">{partNumber}</span>
                          </span>{' '}
                          to{' '}
                          <span className="font-medium">
                            {selected.size} compan{selected.size === 1 ? 'y' : 'ies'}
                          </span>{' '}
                          on PartsBase. It cannot be recalled or edited.
                          {/* The quantity comes from the line item. Showing it here
                              is the last chance to notice a wrong one before 25
                              suppliers quote against it. */}
                          <span className="block text-[11px] text-foreground-muted mt-1">
                            Quantity is taken from this line. Wrong? Close this and fix the line
                            first — every seller will quote against it.
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => send.mutate(armed.key)}
                          disabled={send.isPending}
                          className="btn-primary text-xs px-3 py-1 disabled:opacity-50"
                        >
                          {send.isPending ? 'Sending…' : `Yes, send to ${selected.size}`}
                        </button>
                        <button
                          onClick={() => setArmed(null)}
                          disabled={send.isPending}
                          className="btn-ghost text-xs px-3 py-1 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="text-foreground-muted">
                        {selected.size === 0
                          ? 'Select sellers to send an RFQ.'
                          : `${selected.size} selected`}
                        {overCap && (
                          <span className="text-danger ml-1">
                            — over the {maxRecipients} per-line limit. Deselect some, or raise it in
                            Settings → Automation.
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() =>
                          setArmed({
                            // One key per arming, reused by every retry of this
                            // send. crypto.randomUUID is available in every
                            // browser this app supports.
                            key: crypto.randomUUID(),
                          })
                        }
                        disabled={selected.size === 0 || overCap}
                        className="btn-primary text-xs px-3 py-1 flex items-center gap-1 disabled:opacity-50"
                      >
                        <Send className="w-3 h-3" />
                        Send RFQ{selected.size > 0 ? ` to ${selected.size}` : ''}
                      </button>
                    </div>
                  )}

                  {sendError && <div className="text-danger">{sendError}</div>}
                </div>
              )}

              <p className="text-[11px] leading-relaxed text-foreground-muted pt-1 border-t border-border">
                A dash under unit price means PartsBase reported no price (their &ldquo;call for
                price&rdquo;), not that the part is free; prices carry the seller&rsquo;s own currency
                label, which is not always USD. Condition, quantity and availability are the
                seller&rsquo;s claims and are not verified here.
                {!canSend && (
                  <>
                    {' '}
                    <span className="font-medium text-foreground">No RFQ has been sent</span> from
                    this panel
                    {data.sendBlockedReason === 'role'
                      ? ' — sending requires an owner or admin.'
                      : '.'}
                  </>
                )}
              </p>
              <div className="text-[10px] text-foreground-muted">
                {/* Which of the two this answer was. A metered call and a free
                    cache read look identical otherwise, and the operator is the
                    one paying for the difference. */}
                {data.cached
                  ? `Source: cached PartsBase results${
                      data.cacheAgeMinutes != null ? ` (${data.cacheAgeMinutes} min old)` : ''
                    } — no API call used`
                  : 'Source: PartsBase Real Time Search (live) — 1 API call used'}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
